import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Search, FileText, FileBarChart, CheckCircle, Shield,
  Building2, Users, CreditCard, History, Key, Webhook, RefreshCw,
  Menu, X, LogOut, ChevronDown
} from 'lucide-react';
import { getUser, logout, authAPI } from '../lib/auth';

// ─── Nav groups (Portal-Builder style) ────────────────────────────────────

const platformItems = [
  { path: '/',               label: 'Dashboard',       icon: LayoutDashboard, description: 'Overview and analytics' },
  { path: '/scan',           label: 'Merchant Scan',   icon: Search,          description: 'Scan & classify merchants' },
  { path: '/reports',        label: 'Reports',         icon: FileText,        description: 'Merchant reports & risk history' },
];

const workflowItems = [
  { path: '/workflows',      label: 'Workflows',       icon: CheckCircle,     description: 'Approval workflows' },
  { path: '/report-builder', label: 'Report Builder',  icon: FileBarChart,    description: 'Custom report creation' },
];

const adminItems = [
  { path: '/admin',          label: 'System Admin',    icon: Shield,          description: 'System monitoring',       requireAdmin: true },
  { path: '/tenants',        label: 'Tenants',         icon: Building2,       description: 'Manage organizations',    requirePlatform: true },
  { path: '/users',          label: 'Users & Roles',   icon: Users,           description: 'User management',         requireAdmin: true },
  { path: '/billing',        label: 'Billing',         icon: CreditCard,      description: 'Revenue and invoicing',   requireAdmin: true },
  { path: '/poc-admin',      label: 'POC Admin',       icon: Users,           description: 'POC signups & quota',     requirePlatform: true },
];

const integrationItems = [
  { path: '/api-keys',       label: 'API Keys',        icon: Key,             description: 'External API keys',       requireAdmin: true },
  { path: '/data-push',      label: 'Data Push',       icon: Webhook,         description: 'Webhook & push configs',  requireAdmin: true },
  { path: '/module-sync',    label: 'Module Sync',     icon: RefreshCw,       description: 'Cross-module sync',       requirePlatform: true },
];

function filterItems(items, isAdmin, isPlatform) {
  return items.filter(item => {
    if (item.requirePlatform && !isPlatform) return false;
    if (item.requireAdmin && !isAdmin) return false;
    return true;
  });
}

// PayLegit nav accent — Portal-Builder orange
const NAV_TOP_ACTIVE =
  'text-[#1B365D] bg-gradient-to-b from-[#F5A623] to-[#E8941E] shadow-sm ring-1 ring-[#F5A623]/45';
const NAV_ITEM_ACTIVE =
  'bg-gradient-to-b from-[#F5A623] to-[#E8941E] text-[#1B365D] shadow-sm';
const NAV_ITEM_HOVER = 'hover:bg-[#F5A623]/22 hover:text-[#1B365D]';

// ─── Dropdown component ────────────────────────────────────────────────────

function NavDropdown({ label, items, location }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const hasActive = items.some(item =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
  );

  const handleEnter = () => { clearTimeout(timeoutRef.current); setOpen(true); };
  const handleLeave = () => { timeoutRef.current = setTimeout(() => setOpen(false), 150); };

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 h-9 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
          hasActive
            ? NAV_TOP_ACTIVE
            : `text-slate-700 hover:text-[#1B365D] ${NAV_ITEM_HOVER}`
        }`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-lg min-w-[280px] py-2">
            {items.map(({ path, label: itemLabel, icon: Icon, description }) => {
              const active = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
              return (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setOpen(false)}
                  className={`flex items-start gap-3 px-4 py-2.5 transition-colors rounded-md mx-1 ${
                    active
                      ? NAV_ITEM_ACTIVE
                      : `text-slate-800 ${NAV_ITEM_HOVER}`
                  }`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? 'text-[#1B365D]' : 'text-slate-500'}`} />
                  <div>
                    <div className="text-sm font-semibold leading-tight">{itemLabel}</div>
                    <div className="text-xs text-slate-500 leading-tight mt-0.5">{description}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Navigation ───────────────────────────────────────────────────────

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [user, setUser] = useState(() => getUser());
  const platformAdmin = user?.is_platform_user && ['platform_admin', 'admin'].includes(user?.role);
  const anyAdmin = platformAdmin || user?.role === 'tenant_admin';
  const isPoc = !!user?.is_poc;

  const filteredAdminItems = filterItems(adminItems, anyAdmin, platformAdmin);
  const filteredIntegrationItems = filterItems(integrationItems, anyAdmin, platformAdmin);

  const pocNavItems = [
    { path: '/scan', label: 'Merchant Scan', icon: Search, description: 'Scan & classify merchants' },
    { path: '/reports', label: 'Reports', icon: FileText, description: 'Merchant reports & risk history' },
  ];

  useEffect(() => {
    const syncUser = () => setUser(getUser());
    syncUser();
    window.addEventListener('pl-session-updated', syncUser);
    return () => window.removeEventListener('pl-session-updated', syncUser);
  }, []);

  useEffect(() => {
    if (platformAdmin) {
      authAPI.listTenants()
        .then(res => {
          // Denylist only — never allowlist. paylegit-mcc-online-poc must remain visible.
          const HIDDEN_SLUGS = new Set(['default', 'paylegit', '__platform__']);
          const HIDDEN_NAMES = new Set([
            'default organization',
            'paylegit platform',
            'paylegit-platform',
          ]);
          const CORE_SLUGS = new Set(['paylegit-core', 'paylegit_core', 'core']);
          const cleaned = (res.data.tenants || []).filter((t) => {
            const slug = String(t.tenant_code || t.slug || '').toLowerCase();
            const name = String(t.tenant_name || t.name || '').toLowerCase().trim();
            if (t.is_active === false) return false;
            if (HIDDEN_SLUGS.has(slug) || HIDDEN_NAMES.has(name)) return false;
            // Core is the all-data option (not a separate empty tenant row).
            if (CORE_SLUGS.has(slug)) return false;
            // Show every provisioned customer tenant (ATS, Payu, Demo, POC, …).
            return Boolean(slug);
          });
          setTenants(cleaned);
          // Drop selection if it points at a removed/hidden tenant → Paylegit Core (all).
          const current = localStorage.getItem('viewing_tenant_id');
          if (current && !cleaned.some((t) => String(t.id) === String(current))) {
            localStorage.removeItem('viewing_tenant_id');
            localStorage.removeItem('viewing_tenant_slug');
            localStorage.removeItem('viewing_tenant_name');
          }
        })
        .catch(() => {});
    }
  }, [platformAdmin]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleTenantSwitch = (tenantId) => {
    // 'core' / 'all' = Paylegit Core superadmin aggregate (no tenant filter).
    if (tenantId === 'all' || tenantId === 'core') {
      localStorage.removeItem('viewing_tenant_id');
      localStorage.removeItem('viewing_tenant_slug');
      localStorage.removeItem('viewing_tenant_name');
    } else {
      localStorage.setItem('viewing_tenant_id', tenantId);
      const match = tenants.find((t) => String(t.id) === String(tenantId));
      if (match) {
        localStorage.setItem(
          'viewing_tenant_slug',
          String(match.slug || match.tenant_code || ''),
        );
        localStorage.setItem(
          'viewing_tenant_name',
          String(match.name || match.slug || match.tenant_code || ''),
        );
      }
    }
    setTenantDropdownOpen(false);
    window.location.reload();
  };

  const viewingTenantId = localStorage.getItem('viewing_tenant_id');
  const viewingTenant = viewingTenantId
    ? tenants.find(t => String(t.id) === viewingTenantId)
    : null;

  const allMobileItems = isPoc
    ? [{ group: 'Platform', items: pocNavItems }]
    : [
      { group: 'Platform', items: platformItems },
      { group: 'Workflows', items: workflowItems },
      ...(filteredAdminItems.length ? [{ group: 'Administration', items: filteredAdminItems }] : []),
      ...(filteredIntegrationItems.length ? [{ group: 'Integrations', items: filteredIntegrationItems }] : []),
    ];

  const userInitial = (user?.full_name || user?.email || '?').charAt(0).toUpperCase();
  const userName = user?.full_name || user?.email || 'Admin';
  const userRole = (user?.role || '').replace(/_/g, ' ');

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-slate-200 shadow-sm">
      <div className="max-w-[1920px] mx-auto px-4 md:px-6">
        <div className="flex items-center h-16">

          {/* Logo + brand */}
          <Link to={isPoc ? '/scan' : '/'} className="flex items-center gap-3 mr-8 flex-shrink-0">
            <img src="/logo/PayLegit.png" alt="PayLegit" className="h-10 w-auto" />
            <span className="hidden sm:block text-base font-bold text-[#1B365D] tracking-tight whitespace-nowrap">
              MCC Risk Analysis
            </span>
          </Link>

          {/* Desktop grouped navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {isPoc ? (
              pocNavItems.map(({ path, label, icon: Icon }) => {
                const active = location.pathname.startsWith(path);
                return (
                  <Link
                    key={path}
                    to={path}
                    className={`flex items-center gap-1.5 h-9 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                      active
                        ? NAV_TOP_ACTIVE
                        : `text-slate-700 hover:text-[#1B365D] ${NAV_ITEM_HOVER}`
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </Link>
                );
              })
            ) : (
              <>
                <NavDropdown label="Platform" items={platformItems} location={location} />
                <NavDropdown label="Workflows" items={workflowItems} location={location} />
                {filteredAdminItems.length > 0 && (
                  <NavDropdown label="Administration" items={filteredAdminItems} location={location} />
                )}
                {filteredIntegrationItems.length > 0 && (
                  <NavDropdown label="Integrations" items={filteredIntegrationItems} location={location} />
                )}
              </>
            )}
          </nav>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-3">
            {platformAdmin && (
              <div className="relative">
                <button
                  onClick={() => setTenantDropdownOpen(!tenantDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-[#1B365D]/30 transition-colors"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  {viewingTenant ? viewingTenant.tenant_name : 'Paylegit Core'}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {tenantDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setTenantDropdownOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-40 bg-white border border-gray-200 rounded-xl shadow-lg min-w-[200px] py-1 max-h-64 overflow-auto">
                      <button
                        onClick={() => handleTenantSwitch('core')}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md ${!viewingTenantId ? NAV_ITEM_ACTIVE + ' font-semibold' : `text-slate-700 ${NAV_ITEM_HOVER}`}`}
                      >
                        Paylegit Core
                        <span className="block text-[11px] font-normal text-slate-500">All tenants — superadmin view</span>
                      </button>
                      {tenants.map(t => (
                        <button
                          key={t.id}
                          onClick={() => handleTenantSwitch(String(t.id))}
                          className={`w-full text-left px-3 py-2 text-sm rounded-md ${String(t.id) === viewingTenantId ? NAV_ITEM_ACTIVE + ' font-semibold' : `text-slate-700 ${NAV_ITEM_HOVER}`}`}
                        >
                          {t.tenant_name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 px-2">
              <div className="w-8 h-8 rounded-full bg-[#F5A623]/20 text-[#1B365D] flex items-center justify-center text-xs font-bold flex-shrink-0 ring-1 ring-[#F5A623]/35">
                {userInitial}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 leading-tight truncate max-w-[140px]">{userName}</p>
                {userRole && <p className="text-[11px] text-slate-500 leading-tight capitalize truncate max-w-[140px]">{userRole}</p>}
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden xl:inline">Sign Out</span>
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors ml-auto"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-gray-100 py-3 space-y-4">
            {allMobileItems.map(({ group, items }) => (
              <div key={group} className="space-y-0.5">
                <p className="px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{group}</p>
                {items.map(({ path, label, icon: Icon }) => {
                  const active = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
                  return (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                        active
                          ? NAV_ITEM_ACTIVE
                          : `text-slate-700 ${NAV_ITEM_HOVER}`
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </Link>
                  );
                })}
              </div>
            ))}
            <div className="border-t border-gray-100 pt-3">
              <div className="px-3 py-2 flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#F5A623]/20 text-[#1B365D] flex items-center justify-center text-xs font-bold">
                  {userInitial}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{userName}</p>
                  <p className="text-[11px] text-gray-400 capitalize">{userRole}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 w-full text-left mt-1"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}